import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

/**
 * Shimmer placeholders.
 *
 * A single grey rectangle tells the reader "something is coming" but not *what*, so
 * the page jumps when the data lands. Everything here is built from one primitive
 * and arranged to match the real thing it stands in for — a KPI strip stays a KPI
 * strip, a table keeps its column count — so the swap is a fill-in, not a re-layout.
 */

const SWEEP = 'trShimmer';

/**
 * Base block. Everything below is composed from this.
 *
 * `tone`: 'ink' for light surfaces (cards, the page), 'light' for the dark sidebar —
 * a single fixed grey vanishes on one ground or the other.
 */
export function Shimmer({ width = '100%', height = 14, radius = 8, tone = 'ink', sx, ...rest }) {
  const dark = tone === 'light';
  return (
    <Box
      aria-hidden
      data-shimmer=""
      sx={{
        width,
        height,
        borderRadius: typeof radius === 'number' ? `${radius}px` : radius,
        background: dark ? 'rgba(255, 255, 255, .16)' : 'rgba(11, 11, 11, .075)',
        backgroundImage: `linear-gradient(90deg, transparent 0%, ${
          dark ? 'rgba(255,255,255,.28)' : 'rgba(255,255,255,.6)'
        } 45%, ${dark ? 'rgba(255,255,255,.28)' : 'rgba(255,255,255,.6)'} 55%, transparent 100%)`,
        backgroundSize: '220% 100%',
        backgroundRepeat: 'no-repeat',
        animation: `${SWEEP} 1.35s ease-in-out infinite`,
        // a sweeping highlight is exactly the motion this setting exists to stop
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', backgroundImage: 'none' },
        [`@keyframes ${SWEEP}`]: {
          '0%': { backgroundPosition: '160% 0' },
          '100%': { backgroundPosition: '-60% 0' },
        },
        ...sx,
      }}
      {...rest}
    />
  );
}

/**
 * Inline placeholder for a single value sitting in a line of text — a sidebar
 * badge, a tab count. Sized in `ch` so it holds roughly the width of the number
 * that replaces it, and rendered `inline-block` so it doesn't break the line.
 */
export function ShimmerText({ chars = 3, height = 11, tone, sx }) {
  return (
    <Shimmer
      width={`${chars}ch`}
      height={height}
      radius={4}
      tone={tone}
      sx={{ display: 'inline-block', verticalAlign: 'middle', opacity: 0.7, ...sx }}
    />
  );
}

/** A run of text lines, the last one short so it reads as a paragraph. */
export function ShimmerLines({ lines = 3, height = 12, gap = 1, lastWidth = '60%' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: lines }, (_, i) => (
        <Shimmer key={i} height={height} width={i === lines - 1 ? lastWidth : '100%'} />
      ))}
    </Box>
  );
}

/** The card chrome every panel sits in, so the border doesn't pop in later. */
export function ShimmerCard({ children, sx }) {
  return (
    <Paper
      sx={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)',
        p: '16px',
        height: '100%',
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

/** Matches KpiGrid: same 12-column split, same accent rail, same three text rows. */
export function KpiSkeleton({ columns = 5, count }) {
  const lg = 12 / columns;
  return (
    <Grid container spacing={1.75} sx={{ mb: 2.25 }}>
      {Array.from({ length: count || columns }, (_, i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg }}>
          <ShimmerCard sx={{ position: 'relative', overflow: 'hidden' }}>
            <Shimmer width={4} height="100%" radius={0} sx={{ position: 'absolute', left: 0, top: 0, bottom: 0 }} />
            <Shimmer width="45%" height={9} />
            <Shimmer width="70%" height={26} sx={{ mt: 1.25 }} />
            <Shimmer width="55%" height={10} sx={{ mt: 1 }} />
          </ShimmerCard>
        </Grid>
      ))}
    </Grid>
  );
}

/**
 * Just the rows — for a table already sitting inside a TableCard, where a second
 * card wrapper would draw a border inside a border.
 */
export function TableRowsSkeleton({ columns = 6, rows = 8 }) {
  const widths = Array.from({ length: columns }, (_, i) => (i === 0 ? '70%' : i === columns - 1 ? '45%' : '55%'));
  return (
    <Box aria-hidden>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1.5, p: '10px 14px', background: 'var(--card-2)' }}>
        {widths.map((w, i) => (
          <Shimmer key={i} width={w} height={9} />
        ))}
      </Box>
      {Array.from({ length: rows }, (_, r) => (
        <Box
          key={r}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 1.5,
            p: '13px 14px',
            borderTop: '1px solid var(--grid)',
          }}
        >
          {widths.map((w, i) => (
            <Shimmer key={i} width={w} height={12} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

/** Matches TableCard + .tbl: header rule, then rows on the same column split. */
export function TableSkeleton({ columns = 6, rows = 6, title = true }) {
  const widths = Array.from({ length: columns }, (_, i) => (i === 0 ? '70%' : i === columns - 1 ? '45%' : '55%'));
  return (
    <ShimmerCard sx={{ p: 0, overflow: 'hidden' }}>
      {title && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: '14px 16px' }}>
          <Shimmer width={180} height={13} />
          <Shimmer width={110} height={30} radius={10} />
        </Box>
      )}
      <Box sx={{ borderTop: '1px solid var(--border)' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 2, p: '10px 16px', background: 'var(--card-2)' }}>
          {widths.map((w, i) => (
            <Shimmer key={i} width={w} height={9} />
          ))}
        </Box>
        {Array.from({ length: rows }, (_, r) => (
          <Box
            key={r}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: 2,
              p: '13px 16px',
              borderTop: '1px solid var(--grid)',
            }}
          >
            {widths.map((w, i) => (
              <Shimmer key={i} width={w} height={12} />
            ))}
          </Box>
        ))}
      </Box>
    </ShimmerCard>
  );
}

/** A panel with a heading and a chart well — bars rise from a shared baseline. */
export function ChartSkeleton({ height = 240, bars = 12, heading = true }) {
  // a fixed ramp, not random: a re-render must not make the placeholder twitch
  const ramp = [0.45, 0.7, 0.35, 0.85, 0.55, 0.95, 0.4, 0.75, 0.6, 0.5, 0.8, 0.65];
  return (
    <ShimmerCard>
      {heading && (
        <>
          <Shimmer width="38%" height={14} />
          <Shimmer width="55%" height={10} sx={{ mt: 1 }} />
        </>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height, mt: heading ? 2 : 0 }}>
        {Array.from({ length: bars }, (_, i) => (
          <Shimmer key={i} height={`${Math.round(ramp[i % ramp.length] * 100)}%`} radius={6} sx={{ flex: 1 }} />
        ))}
      </Box>
    </ShimmerCard>
  );
}

/** Label / track / value rows — the geo drill-downs and every ranked list. */
export function BarListSkeleton({ rows = 6, heading = false }) {
  return (
    <Box>
      {heading && <Shimmer width="40%" height={11} sx={{ mb: 1.5 }} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {Array.from({ length: rows }, (_, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '132px 1fr 42px', gap: 1.25, alignItems: 'center' }}>
            <Shimmer height={12} width="85%" />
            <Shimmer height={10} radius={5} />
            <Shimmer height={12} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** The district tile map: same square tiles on the same grid. */
export function TileMapSkeleton({ tiles = 18, columns = 6 }) {
  return (
    <ShimmerCard>
      <Shimmer width="45%" height={14} />
      <Shimmer width="65%" height={10} sx={{ mt: 1 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1, mt: 2 }}>
        {Array.from({ length: tiles }, (_, i) => (
          <Shimmer key={i} height={0} radius={12} sx={{ pb: '100%' }} />
        ))}
      </Box>
    </ShimmerCard>
  );
}

/** Key × district grid, with the label column the real heatmap has. */
export function HeatmapSkeleton({ rows = 10, cols = 18 }) {
  return (
    <ShimmerCard sx={{ overflowX: 'auto' }}>
      <Shimmer width="35%" height={14} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', mt: 2, minWidth: 620 }}>
        {Array.from({ length: rows + 1 }, (_, r) => (
          <Box key={r} sx={{ display: 'grid', gridTemplateColumns: `120px repeat(${cols}, 1fr)`, gap: '3px' }}>
            <Shimmer height={r === 0 ? 10 : 18} width="80%" radius={4} />
            {Array.from({ length: cols }, (_, c) => (
              <Shimmer key={c} height={r === 0 ? 10 : 18} radius={4} />
            ))}
          </Box>
        ))}
      </Box>
    </ShimmerCard>
  );
}

/** Field rows on the 12-column grid — the user form and the lead modal. */
export function FormSkeleton({ rows = 3, fields = 3 }) {
  return (
    <ShimmerCard sx={{ mb: 2 }}>
      <Shimmer width={140} height={14} sx={{ mb: 2 }} />
      {Array.from({ length: rows }, (_, r) => (
        <Grid container spacing={1.75} key={r} sx={{ mb: 1.75 }}>
          {Array.from({ length: fields }, (_, f) => (
            <Grid key={f} size={{ xs: 12, md: 12 / fields }}>
              <Shimmer width="35%" height={9} />
              <Shimmer height={38} radius={12} sx={{ mt: 0.75 }} />
            </Grid>
          ))}
        </Grid>
      ))}
    </ShimmerCard>
  );
}

/** Kanban columns of cards, matching the leads board. */
export function BoardSkeleton({ columns = 7, cards = 2 }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1.5 }}>
      {Array.from({ length: columns }, (_, c) => (
        <Box
          key={c}
          sx={{
            flex: '0 0 238px',
            background: 'var(--card-2)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            p: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '9px',
            minHeight: 160,
          }}
        >
          <Shimmer width="60%" height={10} />
          {Array.from({ length: cards }, (_, i) => (
            <Box key={i} sx={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '10px', p: '11px' }}>
              <Shimmer width="75%" height={13} />
              <Shimmer width="55%" height={10} sx={{ mt: 1 }} />
              <Shimmer width="40%" height={16} sx={{ mt: 1.25 }} />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/** The commission tier editor: name row, base row, tier table, total banner. */
export function TierCardSkeleton({ tiers = 3 }) {
  return (
    <ShimmerCard sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Shimmer width={11} height={11} radius="50%" />
        <Shimmer height={22} sx={{ flex: 1 }} />
        <Shimmer width={72} height={28} radius={10} />
      </Box>
      <Shimmer height={46} radius={10} />
      {Array.from({ length: tiers }, (_, i) => (
        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 74px 1fr 1fr 40px', gap: 1 }}>
          {Array.from({ length: 6 }, (_, c) => (
            <Shimmer key={c} height={26} radius={7} />
          ))}
        </Box>
      ))}
      <Shimmer height={62} radius={10} />
    </ShimmerCard>
  );
}

/** Filter bar: a few pill controls in a row. */
export function ControlsSkeleton({ count = 4 }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 2 }}>
      {Array.from({ length: count }, (_, i) => (
        <Shimmer key={i} width={i === 0 ? 200 : 132} height={36} radius={12} />
      ))}
    </Box>
  );
}

export default Shimmer;
